import { useEffect } from 'react';
import { Form, Input, InputNumber, Select, Button, Row, Col } from '@/shared/ui/primitives';
import { UserSelect } from '@/shared/ui/entity-selects';
import { useTranslation } from 'react-i18next';

const TYPE_OPTIONS = [
  { value: 'MESSAGE', label: 'Message' },
  { value: 'AD_RESPONSE', label: 'Ad Response' },
  { value: 'CHAT', label: 'Chat' },
  { value: 'PRICE_CHANGE', label: 'Price Change' },
  { value: 'VERIFICATION', label: 'Verification' },
  { value: 'AD_EXPIRING', label: 'Ad Expiring' },
  { value: 'SYSTEM', label: 'System' },
];

const ENTITY_TYPE_OPTIONS = [
  { value: 'USER', label: 'User' },
  { value: 'ROOM_AD', label: 'Room Ad' },
  { value: 'ROOM_WANTED_AD', label: 'Room Wanted Ad' },
  { value: 'REPORT', label: 'Report' },
  { value: 'MESSAGE', label: 'Message' },
];

export default function NotificationForm({ initialData, onSubmit, isLoading }: any) {
  const [form] = Form.useForm();
  const { t } = useTranslation();

  useEffect(() => {
    if (initialData) {
      form.setFieldsValue({
        userId: initialData.userId || undefined,
        type: initialData.type || 'SYSTEM',
        titleAr: initialData.titleAr || '',
        titleEn: initialData.titleEn || '',
        descriptionAr: initialData.descriptionAr || '',
        descriptionEn: initialData.descriptionEn || '',
        relatedEntityId: initialData.relatedEntityId || undefined,
        relatedEntityType: initialData.relatedEntityType || '',
        relatedAdId: initialData.relatedAdId || undefined,
        relatedAdType: initialData.relatedAdType || '',
      });
    }
  }, [initialData, form]);

  const handleFinish = (values: any) => {
    const payload: Record<string, unknown> = {
      type: values.type,
      titleAr: values.titleAr,
      titleEn: values.titleEn,
      descriptionAr: values.descriptionAr,
      descriptionEn: values.descriptionEn,
    };
    if (values.userId) payload.userId = Number(values.userId);
    if (values.relatedEntityId) payload.relatedEntityId = Number(values.relatedEntityId);
    if (values.relatedEntityType) payload.relatedEntityType = values.relatedEntityType;
    if (values.relatedAdId) payload.relatedAdId = Number(values.relatedAdId);
    if (values.relatedAdType) payload.relatedAdType = values.relatedAdType;
    onSubmit(payload);
  };

  return (
    <Form form={form} layout="vertical" onFinish={handleFinish} style={{ maxWidth: 600 }} initialValues={{ type: 'SYSTEM' }}>
      {!initialData && (
        <Form.Item label={t('notifications.userId')} name="userId" rules={[{ required: true }]}>
          <UserSelect placeholder={t('notifications.userId')} />
        </Form.Item>
      )}

      <Form.Item label={t('common.type')} name="type" rules={[{ required: true }]}>
        <Select options={TYPE_OPTIONS} />
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label={t('notifications.titleEn')} name="titleEn" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label={t('notifications.titleAr')} name="titleAr" rules={[{ required: true }]}>
            <Input dir="rtl" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label={t('notifications.descriptionEn')} name="descriptionEn" rules={[{ required: true }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label={t('notifications.descriptionAr')} name="descriptionAr" rules={[{ required: true }]}>
            <Input.TextArea rows={3} dir="rtl" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label={t('notifications.relatedEntityType')} name="relatedEntityType">
            <Select allowClear options={ENTITY_TYPE_OPTIONS} placeholder="Select entity type" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label={t('notifications.relatedEntityId')} name="relatedEntityId"
            tooltip="Technical field — ID of the linked entity (user, ad, report…). Used for deep-linking notifications.">
            <InputNumber style={{ width: '100%' }} min={1} />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item>
        <Button type="primary" htmlType="submit" loading={isLoading}>
          {initialData ? t('common.update') : t('common.create')}
        </Button>
      </Form.Item>
    </Form>
  );
}
